// Share-link queries (PRD §9.5 + docs/frontend_architecture.md §5,
// B2 PR3 / B2c + B2 PR4 / B2d + B2 PR5 / B2e + B2 PR6 / B2f).
//
// `useShareAssetUrl` mints a presigned playback URL for one asset on
// one candidate visible through one share link. `useShareComments`
// reads the comment list on a candidate visible through the same
// link. `useShareAnnotations` reads the annotation set on a candidate
// at a specific frame — the frame **is** in the query key because the
// backend filters server-side, and switching frames must swap caches
// (PRD §9.5 reading 5, B2e scope: "switching frames swaps the
// annotation set"). All query keys include (token, candidateId, …) so
// the cache is naturally scoped to a single viewer session — no
// cross-link bleed.
//
// `password` travels in the `X-Share-Password` header (never a query
// param - query strings land in access logs and browser history; the
// backend keeps `?password=` only as a deprecated fallback). Callers
// pass it down from <ShareGate> once the viewer has unlocked a
// password-protected link. We deliberately don't include `password` in
// the query key: a viewer session unlocks once and re-uses the same
// password for every read, so keying on it would only fragment the
// cache on a single-session value that never changes.

import { skipToken, useQuery, type UseQueryResult } from '@tanstack/react-query';

import { client } from '../client';
import type { components } from '../schema';

export type ShareAssetPlaybackResponse =
  components['schemas']['ShareAssetPlaybackResponse'];
export type CommentOut = components['schemas']['CommentOut'];
export type AnnotationOut = components['schemas']['AnnotationOut'];
export type CandidateOut = components['schemas']['CandidateOut'];


function passwordHeaders(password: string | null): Record<string, string> {
  return password !== null ? { 'X-Share-Password': password } : {};
}

export const shareKeys = {
  all: ['share'] as const,
  asset: (token: string, candidateId: string, assetId: string) =>
    [...shareKeys.all, 'asset', token, candidateId, assetId] as const,
  comments: (token: string, candidateId: string) =>
    [...shareKeys.all, 'comments', token, candidateId] as const,
  annotations: (token: string, candidateId: string, frame: number) =>
    [...shareKeys.all, 'annotations', token, candidateId, frame] as const,
  candidate: (token: string, candidateId: string) =>
    [...shareKeys.all, 'candidate', token, candidateId] as const,
};

async function fetchAssetUrl(
  token: string,
  candidateId: string,
  assetId: string,
  password: string | null
): Promise<ShareAssetPlaybackResponse> {
  const { data } = await client.GET(
    '/api/v1/share/{token}/candidates/{candidate_id}/assets/{asset_id}/url',
    {
      params: {
        path: { token, candidate_id: candidateId, asset_id: assetId },
      },
      headers: passwordHeaders(password),
    }
  );
  if (!data) throw new Error('useShareAssetUrl: empty response');
  return data;
}

export function useShareAssetUrl(
  token: string | undefined,
  candidateId: string | undefined,
  assetId: string | undefined,
  password: string | null = null
): UseQueryResult<ShareAssetPlaybackResponse> {
  return useQuery({
    queryKey:
      token && candidateId && assetId
        ? shareKeys.asset(token, candidateId, assetId)
        : shareKeys.all,
    queryFn:
      token && candidateId && assetId
        ? () => fetchAssetUrl(token, candidateId, assetId, password)
        : skipToken,
    // Presigned URLs default to 1h TTL on the backend
    // (settings.s3_presigned_ttl). Refresh ahead of expiry so a long-
    // lived viewer session doesn't catch a 403 mid-scrub.
    staleTime: 50 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });
}

async function fetchComments(
  token: string,
  candidateId: string,
  password: string | null
): Promise<CommentOut[]> {
  const { data } = await client.GET(
    '/api/v1/share/{token}/candidates/{candidate_id}/comments',
    {
      params: {
        path: { token, candidate_id: candidateId },
      },
      headers: passwordHeaders(password),
    }
  );
  if (!data) throw new Error('useShareComments: empty response');
  return data;
}

export function useShareComments(
  token: string | undefined,
  candidateId: string | undefined,
  password: string | null = null
): UseQueryResult<CommentOut[]> {
  return useQuery({
    queryKey:
      token && candidateId
        ? shareKeys.comments(token, candidateId)
        : shareKeys.all,
    queryFn:
      token && candidateId
        ? () => fetchComments(token, candidateId, password)
        : skipToken,
  });
}

async function fetchAnnotations(
  token: string,
  candidateId: string,
  frame: number,
  password: string | null
): Promise<AnnotationOut[]> {
  const { data } = await client.GET(
    '/api/v1/share/{token}/candidates/{candidate_id}/annotations',
    {
      params: {
        path: { token, candidate_id: candidateId },
        query: { frame },
      },
      headers: passwordHeaders(password),
    }
  );
  if (!data) throw new Error('useShareAnnotations: empty response');
  return data;
}

export function useShareAnnotations(
  token: string | undefined,
  candidateId: string | undefined,
  frame: number,
  password: string | null = null
): UseQueryResult<AnnotationOut[]> {
  return useQuery({
    queryKey:
      token && candidateId
        ? shareKeys.annotations(token, candidateId, frame)
        : shareKeys.all,
    queryFn:
      token && candidateId
        ? () => fetchAnnotations(token, candidateId, frame, password)
        : skipToken,
  });
}

async function fetchShareCandidate(
  token: string,
  candidateId: string,
  password: string | null
): Promise<CandidateOut> {
  const { data } = await client.GET(
    '/api/v1/share/{token}/candidates/{candidate_id}',
    {
      params: {
        path: { token, candidate_id: candidateId },
      },
      headers: passwordHeaders(password),
    }
  );
  if (!data) throw new Error('useShareCandidate: empty response');
  return data;
}

export function useShareCandidate(
  token: string | undefined,
  candidateId: string | undefined,
  password: string | null = null
): UseQueryResult<CandidateOut> {
  return useQuery({
    queryKey:
      token && candidateId
        ? shareKeys.candidate(token, candidateId)
        : shareKeys.all,
    queryFn:
      token && candidateId
        ? () => fetchShareCandidate(token, candidateId, password)
        : skipToken,
    // Candidate metadata is stable across a viewer session; let it sit
    // in cache so flipping between playlist tiles is instant after the
    // first load.
    staleTime: 5 * 60 * 1000,
  });
}
